import React, {
    ReactElement,
    useMemo,
} from "react";
import {
    ChatSettings,
    ChatSettingsArtificialIntelligence,
    ChatSettingsChannelEmail,
    ChatSettingsDeflection,
    ChatSettingsIdentitiesType,
    ChatSettingsMicrosoftTeams,
    ChatSettingsOptionsInput, ChatSettingsSlack,
    GetChatSettingsDocument, GetSlackChannelSettingsDocument,
    IntegrationActionType, Settings,
    useGetChatSettingsQuery, useSaveChatSettingsMutation,
} from "../../../../__generated__/api";
import {
    removeTypename,
} from "../../../../api";
import Editor from "../../../../components/agent-v3/editor/Editor";
import { ProxyProperty, useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import AdvancedSection from "../../../../components/expanding-section/AdvancedSection";
import Integrations from "../../../../components/pickers/integrations/Integrations";
import RequestTypes from "../../../../components/pickers/request-types/RequestTypes";
import Select from "../../../../components/select/Select";
import SettingsContainer from "../../../../components/theme/settings";
import useRouting, { useParams } from "../../../../contexts/Routing";
import { ErrorRemote } from "../../../error/Error";
import { DEEP_COMPARER, SettingsProps } from "../base";
import ChannelMenuSettings from "./settings/ChannelMenuV3";
import DeflectionSettings from "./settings/DeflectionV3";
import EmailSettings from "./settings/EmailV3";
import MicrosoftTeamsSettings from "./settings/MicrosoftTeamsV3";
import SlackSettings from "./settings/SlackV3";
import { WELCOME_VARIABLES } from "../variables";
import ArtificialIntelligenceSettings from "./settings/ArtificialIntelligence";
import usePortal from "../../../../contexts/PortalContext";

export function DisplayChatSettings({
    chatSettings,
    settings,
    // onChange,
    isLoading,
    shouldAllowSlack,
    shouldAllowMicrosoftTeams,
    shouldAllowEmail,
}: {
    chatSettings: ProxyProperty<ChatSettings>;
    settings: Settings | undefined;
    // onChange: (input: ChatSettingsInput) => void;
    isLoading: boolean;
    shouldAllowSlack?: boolean;
    shouldAllowMicrosoftTeams?: boolean;
    shouldAllowEmail?: boolean;
}): ReactElement {
    const { isAIEnabled } = usePortal();

    const properties = chatSettings.useBuilder(
        builder => builder
            .array("formIds", current => current?.formIds ?? [])
            .property("welcomeMessage", current => current?.welcomeMessage)
            .array("identities", current => (current?.identities ?? []) as string[])
            .property("identitiesType", current => current?.identitiesType ?? ChatSettingsIdentitiesType.Inherit)
            .property("slaTierId", current => current?.slaTierId as string)
            .proxy("options", current => current?.options as ChatSettingsOptionsInput),
    );

    const optionsProperties = properties.options.useBuilder(builder => builder
        .proxy("slack", current => current?.slack as ChatSettingsSlack)
        .proxy("channelMenu", current => current?.channelMenu)
        .proxy("microsoftTeams", current => current?.microsoftTeams as ChatSettingsMicrosoftTeams)
        .proxy("email", current => current?.email as ChatSettingsChannelEmail)
        .proxy("deflection", current => current?.deflection as ChatSettingsDeflection)
        .proxy("artificialIntelligence", current => current?.artificialIntelligence as ChatSettingsArtificialIntelligence));

    const currentSlaTier = useMemo(
        () => (properties.slaTierId.value
            ? settings?.sla?.tiers?.find(tier => tier.id === properties.slaTierId.value)
            : undefined),
        [properties.slaTierId.value],
    );

    return (
        <>
            <SettingsContainer.Section
                title="Channel Defaults"
                helpText="These general settings help you manage this specific channel and its overrides"
            >
                <SettingsContainer.Setting
                    title="Request Types"
                    description="When a user asks a question, thse are the request types the user can fill out.
                        To order the request types, click and drag them inside this box."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <RequestTypes
                        isMulti
                        placeholder=""
                        onSelected={properties.formIds.setValue}
                        value={properties.formIds.value}
                    />
                </SettingsContainer.Setting>

                <AdvancedSection title="Advanced Settings">

                    <SettingsContainer.Setting
                        title="First Response Message"
                        description={`When a customer first asks a question, this is the message they will
                            first see in this channel. If you want to overwrite with no message,
                            make the defaut message "none".`}
                        descriptionElement={(
                            <>
                                When a customer first asks a question, this is the message they will
                                first see in this channel. If you want to overwrite with no message,
                                make the defaut message &quot;none&quot;.
                            </>
                        )}
                        permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                        isLoading={isLoading}
                    >
                        <Editor
                            value={properties.welcomeMessage.value?.rawText ?? ""}
                            onChange={text => properties.welcomeMessage.setValue({ rawText: text })}
                            size="medium"
                            hasBorder
                            placeholder="Hey, please remember to use a thread..."
                            variables={WELCOME_VARIABLES}
                        />
                    </SettingsContainer.Setting>

                    <SettingsContainer.Setting
                        title="Ticket Integration"
                        description="When creating a new ticket, Agent can automatically create a ticket in a 3rd party
                            services such as Zendesk, Intercom, Atlassian, or something else. Which existing
                            integration would you like to use for this?"
                        permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                        isLoading={isLoading}
                    >
                        <Integrations
                            placeholder="Which integration should create a ticket"
                            onSelected={(selected, type) => {
                                properties.identities.setValue(selected ? [selected] : []);
                                properties.identitiesType.setValue(type);
                            }}
                            canSetNone
                            isInheritable
                            className={SettingsContainer.Setting.PickerClassName}
                            identitiesType={properties.identitiesType.value}
                            value={properties.identities.value.find(identity => identity.startsWith("urn:fq:integration"))}
                            actions={[IntegrationActionType.CreateTicket]}
                        />
                    </SettingsContainer.Setting>

                    {settings?.sla?.tiers?.isNotEmpty && (
                        <SettingsContainer.Setting
                            title="SLA Tier"
                            description="Which SLA Tier should be applied by default."
                            permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                            isLoading={isLoading}
                        >

                            <Select
                                options={settings?.sla?.tiers?.map(tier => ({
                                    label: tier.name as string,
                                    value: tier.id as string,
                                    data: tier,
                                })) ?? []}
                                isClearable
                                onChange={tier => {
                                    properties.slaTierId.setValue(tier?.data?.id);
                                }}
                                className={SettingsContainer.Setting.PickerClassName}
                                value={!currentSlaTier ? null : {
                                    label: currentSlaTier.name as string,
                                    value: currentSlaTier.id as string,
                                    data: currentSlaTier,
                                }}
                            />
                        </SettingsContainer.Setting>
                    )}

                </AdvancedSection>
            </SettingsContainer.Section>

            {(optionsProperties.slack.cleanValue || shouldAllowSlack) && (
                <>
                    <SlackSettings
                        data={optionsProperties.slack}
                        tag={chatSettings.value?.options?.tag}
                    />

                    <ChannelMenuSettings
                        data={optionsProperties.channelMenu}
                    />
                </>
            )}

            {(optionsProperties.microsoftTeams.cleanValue || shouldAllowMicrosoftTeams) && (
                <>
                    <MicrosoftTeamsSettings
                        data={optionsProperties.microsoftTeams}
                        canAddTags={chatSettings.value?.options?.tag == null}
                    />

                    <ChannelMenuSettings data={optionsProperties.channelMenu} />
                </>
            )}

            {(optionsProperties.email.cleanValue || shouldAllowEmail) && (
                <EmailSettings data={optionsProperties.email} />
            )}

            {isAIEnabled && chatSettings && (
                <ArtificialIntelligenceSettings data={optionsProperties.artificialIntelligence} />
            )}

            {chatSettings && (
                <DeflectionSettings data={optionsProperties.deflection} />
            )}

        </>

    );
}

DisplayChatSettings.defaultProps = {
    shouldAllowSlack: false,
    shouldAllowEmail: false,
    shouldAllowMicrosoftTeams: false,
};

export default function Chat(props: SettingsProps): ReactElement {
    const { Paths } = useRouting();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const [save] = useSaveChatSettingsMutation();

    const { data, error, loading: isLoading } = useGetChatSettingsQuery({
        fetchPolicy: "no-cache",
        variables: {
            id,
        },
    });

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .proxy("chatSettings", () => data?.chatSettings as ChatSettings, DEEP_COMPARER),
        [data?.chatSettings],
    );

    const { element } = tracker.useDirtyTracker({
        finishedSavingMessage: "Channel Settings Saved",
        onSave: async (__, onDone) => {
            await save({
                refetchQueries: [
                    { query: GetSlackChannelSettingsDocument },
                    { query: GetChatSettingsDocument, variables: { id } },
                ],
                variables: {
                    id,
                    settings: removeTypename(properties.chatSettings.serialize() as ChatSettings),
                },
            });
            onDone();
        },
    });

    if (error) {
        return <ErrorRemote error={error} />;
    }

    return (
        <>
            <SettingsContainer>
                <DisplayChatSettings
                    chatSettings={properties.chatSettings}
                    settings={props.settings}
                    isLoading={isLoading}
                />
            </SettingsContainer>
            {element}
        </>

    );
}
